import { FC } from 'react';
import { alpha, Card, CardContent, Grid, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AutoReturnAndResendTab } from '../autoReturnAndResend.types';
import { ARAR } from '../autoReturnAndResend.classes';

interface SummaryCardConfig {
    tab: AutoReturnAndResendTab;
    count: number;
    labelKey: string;
    colorKey: 'primary' | 'warning' | 'info' | 'error';
}

interface AutoReturnAndResendSummaryCardsProps {
    activeTab: AutoReturnAndResendTab;
    resendCount: number;
    autoReturnCount: number;
    processedCount: number;
    notFoundCount: number;
    onTabChange: (tab: AutoReturnAndResendTab) => void;
}

export const AutoReturnAndResendSummaryCards: FC<AutoReturnAndResendSummaryCardsProps> = ({
    activeTab,
    resendCount,
    autoReturnCount,
    processedCount,
    notFoundCount,
    onTabChange,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const cards: SummaryCardConfig[] = [
        { tab: 'resend', count: resendCount, labelKey: 'autoReturnAndResend.tabs.resend', colorKey: 'primary' },
        { tab: 'autoReturn', count: autoReturnCount, labelKey: 'autoReturnAndResend.tabs.autoReturn', colorKey: 'warning' },
        { tab: 'processed', count: processedCount, labelKey: 'autoReturnAndResend.tabs.processed', colorKey: 'info' },
        { tab: 'notFound', count: notFoundCount, labelKey: 'autoReturnAndResend.tabs.notFound', colorKey: 'error' },
    ];

    return (
        <Grid className={ARAR.summarySection} container spacing={2} sx={{ width: '100%' }}>
            {cards.map(({ tab, count, labelKey, colorKey }) => {
                const color = theme.palette[colorKey].main;
                const isActive = activeTab === tab;

                return (
                    <Grid key={tab} size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex' }}>
                        <Card
                            className={ARAR.summaryCard}
                            variant={isActive ? 'outlined' : 'elevation'}
                            elevation={isActive ? 0 : 1}
                            onClick={() => onTabChange(tab)}
                            sx={{
                                flex: 1,
                                cursor: 'pointer',
                                borderLeft: `3px solid ${color}`,
                                backgroundColor: isActive ? alpha(color, 0.08) : 'background.paper',
                                transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': {
                                    backgroundColor: alpha(color, 0.12),
                                },
                            }}
                        >
                            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                                <Typography variant="h4" sx={{ fontWeight: 700, color, mb: 0.5 }}>
                                    {count}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {t(labelKey)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
};
